import { CheckCircleFilled, LoadingOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons'
import { Button, Input, Tag, Tooltip, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { siteAssetVariantUrl, type VideoKeyframeAssetV1, type VideoKeyframeDesignBatchV1 } from '../../api'

const STATUS_CLASS: Record<string, string> = {
  not_generated: 'tc-badge-neutral',
  queued: 'tc-badge-warning',
  running: 'tc-badge-info',
  succeeded: 'tc-badge-success',
  failed: 'tc-badge-error',
  blocked_by_dependency: 'tc-badge-neutral',
  needs_regeneration: 'tc-badge-warning',
}

function KeyframeCard({
  workspaceId,
  asset,
  onGenerate,
  onRegenerate,
  onEditPrompt,
  busy,
}: {
  workspaceId: string
  asset: VideoKeyframeAssetV1
  onGenerate: (keyframeId: string) => void
  onRegenerate: (keyframeId: string) => void
  onEditPrompt: (keyframeId: string, prompt: string) => void
  busy: boolean
}) {
  const { t } = useTranslation(['video', 'actions'])
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(asset.spec.generation_prompt_semantic)
  const spec = asset.spec
  const canGenerate = asset.status === 'not_generated' || asset.status === 'failed' || asset.status === 'needs_regeneration'
  const canRegenerate = asset.status === 'succeeded'
  const isActive = asset.status === 'queued' || asset.status === 'running'
  const roleLabel = t(`director.keyframes.roles.${asset.role}`)
  const statusLabel = t(`director.keyframes.status.${asset.status}`, { defaultValue: asset.status })

  return (
    <div className="tc-vkf-card" data-testid={`keyframe-card-${asset.keyframe_id}`}>
      <div className="tc-vkf-card-head">
        <span className="tc-vdc-shot-badge">{roleLabel}</span>
        <span className={`tc-badge ${STATUS_CLASS[asset.status] || 'tc-badge-neutral'}`}>
          {isActive && <LoadingOutlined style={{ marginRight: 4 }} />}
          {statusLabel}
        </span>
      </div>

      <div className="tc-vkf-card-image">
        {asset.status === 'succeeded' && asset.output_asset_id ? (
          <img src={siteAssetVariantUrl(workspaceId, asset.output_asset_id, 'thumbnail')} alt={roleLabel} />
        ) : isActive ? (
          <div className="tc-vkf-card-image-placeholder">
            <LoadingOutlined className="tc-vdc-spin" /> {t('video:director.keyframes.generating')}
          </div>
        ) : asset.status === 'blocked_by_dependency' ? (
          <div className="tc-vkf-card-image-placeholder">
            <WarningOutlined /> {t('video:director.keyframes.waitingUpstream')}
          </div>
        ) : (
          <div className="tc-vkf-card-image-placeholder">{t('video:director.keyframes.noImage')}</div>
        )}
      </div>

      <div className="tc-vkf-card-desc" title={spec.visual_description}>
        {spec.visual_description}
      </div>

      {editing ? (
        <div className="tc-vkf-prompt-edit">
          <Input.TextArea value={draft} onChange={(e) => setDraft(e.target.value)} autoSize={{ minRows: 2, maxRows: 4 }} />
          <div className="tc-vkf-prompt-edit-actions">
            <Button
              size="small"
              type="primary"
              onClick={() => {
                onEditPrompt(asset.keyframe_id, draft)
                setEditing(false)
              }}
            >
              {t('actions:save')}
            </Button>
            <Button size="small" onClick={() => setEditing(false)}>
              {t('actions:cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Tooltip title={t('video:director.keyframes.editPromptTooltip')} overlayClassName="tc-video-gen-tooltip">
          <div className="tc-vkf-prompt" onClick={() => setEditing(true)}>
            {spec.generation_prompt_semantic}
          </div>
        </Tooltip>
      )}

      {asset.error_message && <div className="tc-vdc-error-banner">{asset.error_message}</div>}

      <div className="tc-vkf-card-actions">
        {canGenerate && (
          <Button size="small" type="primary" loading={busy} onClick={() => onGenerate(asset.keyframe_id)}>
            {t('video:director.keyframes.generate')}
          </Button>
        )}
        {canRegenerate && (
          <Button size="small" icon={<ReloadOutlined />} loading={busy} onClick={() => onRegenerate(asset.keyframe_id)}>
            {t('video:director.keyframes.regenerate')}
          </Button>
        )}
        {asset.status === 'blocked_by_dependency' && (
          <span className="tc-vdc-panel-disabled-hint">{t('video:director.keyframes.dependencyBlocked')}</span>
        )}
      </div>
    </div>
  )
}

export default function KeyframeDesignPanel({
  workspaceId,
  storyboardTitle,
  loading,
  status,
  keyframeDesignStale,
  activeBatch,
  allRequiredValid,
  confirmedBatchId,
  errorMessage,
  busyKeyframeId,
  onDesign,
  onRedesign,
  onGenerateFrame,
  onRegenerateFrame,
  onGenerateRequired,
  onEditPrompt,
  onConfirm,
  disabledReason,
}: {
  workspaceId: string
  storyboardTitle: string | null
  loading?: boolean
  status: string
  keyframeDesignStale: boolean
  activeBatch: VideoKeyframeDesignBatchV1 | null
  allRequiredValid: boolean
  confirmedBatchId: string | null
  errorMessage: string | null
  busyKeyframeId: string | null
  onDesign: () => void
  onRedesign: () => void
  onGenerateFrame: (keyframeId: string) => void
  onRegenerateFrame: (keyframeId: string) => void
  onGenerateRequired: () => void
  onEditPrompt: (keyframeId: string, prompt: string) => void
  onConfirm: () => void
  disabledReason: string | null
}) {
  const { t } = useTranslation('video')
  const designing = status === 'queued' || status === 'running'
  const hasDesign = !!activeBatch?.keyframes?.length
  const bible = activeBatch?.global_visual_bible

  if (loading) {
    return (
      <div className="tc-vdc-panel-empty" data-testid="keyframe-panel-loading">
        <div className="tc-vdc-panel-empty-title">
          <LoadingOutlined className="tc-vdc-spin" /> {t('director.keyframes.loading')}
        </div>
      </div>
    )
  }

  if (disabledReason) {
    return <div className="tc-vdc-panel-empty">{disabledReason}</div>
  }

  const shots = new Map<string, VideoKeyframeAssetV1[]>()
  for (const kf of activeBatch?.keyframes ?? []) {
    const list = shots.get(kf.shot_id) ?? []
    list.push(kf)
    shots.set(kf.shot_id, list)
  }
  const roleOrder: Record<string, number> = { START: 0, MIDDLE: 1, END: 2 }
  for (const list of shots.values()) list.sort((a, b) => (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9))

  return (
    <div className="tc-vdc-storyboard-panel">
      {storyboardTitle && (
        <div className="tc-vdc-selected-proposal-summary">
          <div className="tc-vdc-panel-sub">{t('director.keyframes.confirmedStoryboard')}</div>
          <div className="tc-vdc-concept-title">{storyboardTitle}</div>
        </div>
      )}

      {keyframeDesignStale && <div className="tc-vdc-stale-banner">{t('director.keyframes.staleBanner')}</div>}
      {status === 'failed' && <div className="tc-vdc-error-banner">{errorMessage || t('director.keyframes.designFailed')}</div>}

      {!hasDesign && !designing && (
        <div className="tc-vdc-panel-empty">
          <Button type="primary" onClick={onDesign}>
            {t('director.keyframes.designButton')}
          </Button>
          <div className="tc-vdc-panel-empty-desc">{t('director.keyframes.designDesc')}</div>
        </div>
      )}

      {designing && (
        <div className="tc-vdc-panel-empty">
          <div className="tc-vdc-panel-empty-title">
            <LoadingOutlined className="tc-vdc-spin" /> {t('director.keyframes.designing')}
          </div>
        </div>
      )}

      {hasDesign && bible && (
        <div className="tc-vkf-bible">
          <div className="tc-vdc-panel-sub">{t('director.keyframes.visualBible')}</div>
          <div className="tc-vkf-bible-grid">
            <div>
              <strong>{t('director.keyframes.bible.subjectIdentity')}：</strong>
              {bible.subject_identity}
            </div>
            <div>
              <strong>{t('director.keyframes.bible.appearanceConstraints')}：</strong>
              {bible.appearance_constraints}
            </div>
            <div>
              <strong>{t('director.keyframes.bible.environment')}：</strong>
              {bible.environment_identity}
            </div>
            <div>
              <strong>{t('director.keyframes.bible.colorLighting')}：</strong>
              {bible.color_and_lighting}
            </div>
            <div>
              <strong>{t('director.keyframes.bible.visualStyle')}：</strong>
              {bible.visual_style}
            </div>
            <div>
              <strong>{t('director.keyframes.bible.continuityRules')}：</strong>
              {bible.continuity_rules}
            </div>
          </div>
        </div>
      )}

      {hasDesign && (
        <>
          {activeBatch?.warnings && activeBatch.warnings.length > 0 && (
            <div className="tc-vdc-stale-banner">
              {activeBatch.warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          )}

          <div className="tc-vkf-shots">
            {[...shots.entries()].map(([shotId, kfs]) => (
              <div key={shotId} className="tc-vkf-shot-group">
                <div className="tc-vdc-shot-badge">SHOT {shotId.replace(/^shot_?/i, '')}</div>
                <div className="tc-vkf-shot-cards">
                  {kfs.map((kf) => (
                    <KeyframeCard
                      key={kf.keyframe_id}
                      workspaceId={workspaceId}
                      asset={kf}
                      busy={busyKeyframeId === kf.keyframe_id}
                      onGenerate={onGenerateFrame}
                      onRegenerate={onRegenerateFrame}
                      onEditPrompt={onEditPrompt}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="tc-vdc-panel-regen-row">
            <Button onClick={onRedesign} disabled={designing}>
              {t('director.keyframes.redesign')}
            </Button>
            <Button onClick={onGenerateRequired} disabled={designing}>
              {t('director.keyframes.generateAllPending')}
            </Button>
            <Button
              type="primary"
              icon={confirmedBatchId ? <CheckCircleFilled /> : undefined}
              onClick={() => {
                if (!allRequiredValid) {
                  message.warning(t('director.keyframes.confirmWarning'))
                  return
                }
                onConfirm()
              }}
              disabled={designing || keyframeDesignStale || !allRequiredValid}
            >
              {confirmedBatchId ? t('director.keyframes.confirmed') : t('director.keyframes.confirm')}
            </Button>
          </div>
          {activeBatch?.shot_continuity_links && activeBatch.shot_continuity_links.length > 0 && (
            <div className="tc-vkf-continuity">
              <div className="tc-vdc-panel-sub">{t('director.keyframes.shotContinuity')}</div>
              {activeBatch.shot_continuity_links.map((link, i) => (
                <Tag key={i}>
                  {link.from_keyframe_id} → {link.to_keyframe_id} ({link.continuity_type})
                </Tag>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
